import AntIcon from "@/components/icons/AntIcon";
import { selectStandardProps } from "@/utils/componentUtils";
import { skipInitialFormValues } from "@/utils/formUtils";
import { URLQuery } from "@/utils/queryUtils";
import { validationConstants, validations } from "@/utils/validationUtils";
import { Button, Card, Col, Form, Input, Row, Select } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { rowGutter } from "../../../../../common/constants";
import { VehicleCategory } from "../../../../contract/enums";
import { DEFAULT_VEHICLE_CATEGORY_QUERY_PARAMS } from "../../api";
import { CertificateVehicleCategoryFilterPageRequest } from "../../types";

const colSpan = 6;

interface Props {
  searchParams: CertificateVehicleCategoryFilterPageRequest;
  onCreateClick: () => void;
}

export const VehicleCategoryFilterForm = ({ searchParams, onCreateClick }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [form] = Form.useForm<CertificateVehicleCategoryFilterPageRequest>();

  useEffect(() => {
    form.setFieldsValue({ keyword: searchParams.keyword, categories: searchParams.categories });
  }, [searchParams, form]);

  const onFilterSubmit = (values: CertificateVehicleCategoryFilterPageRequest) => {
    navigate(
      {
        search: URLQuery.searchParamsToString({
          searchParams: {
            ...values,
            ...skipInitialFormValues(values, DEFAULT_VEHICLE_CATEGORY_QUERY_PARAMS),
            pageIndex: undefined,
          },
        }),
      },
      { replace: true },
    );
  };

  return (
    <Card
      className="card-box"
      title={<h2>{t("vehicleCategory.titles.pageTitle")}</h2>}
      extra={
        <Button type="primary" icon={<AntIcon type="plus" />} onClick={onCreateClick}>
          {t("vehicleCategory.actions.createCategory")}
        </Button>
      }
    >
      <Form form={form} onFinish={onFilterSubmit} layout="vertical" name="vehicleCategorySearchForm">
        <Row gutter={rowGutter} justify="center">
          <Col span={colSpan}>
            <Form.Item
              name="keyword"
              label={t("common.search")}
              rules={[
                validations.size(
                  validationConstants.SEARCH_KEYWORD_MIN_LENGTH,
                  validationConstants.SEARCH_KEYWORD_MAX_LENGTH,
                ),
              ]}
            >
              <Input allowClear placeholder={t("vehicleCategory.helpers.filterSearchPlaceholder")} />
            </Form.Item>
          </Col>

          <Col span={colSpan}>
            <Form.Item name="categories" label={t("contract.enums.vehicleCategory._label")} rules={[validations.none]}>
              <Select
                {...selectStandardProps}
                mode="multiple"
                maxTagCount="responsive"
                allowClear
                options={Object.keys(VehicleCategory).map((category) => ({
                  value: category,
                  label: t("contract.enums.vehicleCategory." + category),
                }))}
              />
            </Form.Item>
          </Col>

          <Col flex="110px" className="no-title-space">
            <Form.Item>
              <Button type="primary" htmlType="submit" icon={<AntIcon type="search" />}>
                {t("common.filter")}
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Card>
  );
};
